import React, { useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold, Italic, Strikethrough, Code,
  List, ListOrdered, Quote, Minus,
  Heading1, Heading2, Heading3,
  Image as ImageIcon, Link as LinkIcon,
  Undo, Redo, Upload, Loader2,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded-lg transition-colors ${
      isActive
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const uploadImageFile = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`${API_URL}/api/upload/image`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || 'Upload failed');
  }
  const data = await r.json();
  return data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`;
};

// Apply a wrap/float class to the currently-selected image node.
const applyImageWrap = (editor, wrap) => {
  if (!editor.isActive('image')) return;
  // wrap: 'inline' | 'left' | 'right' | 'center'
  const wrapClassMap = {
    inline: 'tiptap-img-inline',
    left: 'tiptap-img-left',
    right: 'tiptap-img-right',
    center: 'tiptap-img-center',
  };
  const baseClass = 'tiptap-img';
  const newClass = `${baseClass} ${wrapClassMap[wrap]}`;
  editor.chain().focus().updateAttributes('image', { class: newClass }).run();
};

const MenuBar = ({ editor }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = React.useState(false);

  if (!editor) return null;

  const triggerUpload = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImageFile(file);
      editor.chain().focus().setImage({ src: url, class: 'tiptap-img tiptap-img-inline' }).run();
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const addImageUrl = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url, class: 'tiptap-img tiptap-img-inline' }).run();
    }
  };

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const imageSelected = editor.isActive('image');

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-xl">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleFile}
      />

      {/* Undo/Redo */}
      <MenuButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo size={18} /></MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Headings */}
      <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="Heading 1"><Heading1 size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="Heading 3"><Heading3 size={18} /></MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Text formatting */}
      <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold"><Bold size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic"><Italic size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough"><Strikethrough size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().toggleCode().run()} isActive={editor.isActive('code')} title="Code"><Code size={18} /></MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Text alignment */}
      <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align Left"><AlignLeft size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align Center"><AlignCenter size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align Right"><AlignRight size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} isActive={editor.isActive({ textAlign: 'justify' })} title="Justify"><AlignJustify size={18} /></MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Lists */}
      <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List"><List size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List"><ListOrdered size={18} /></MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Block */}
      <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Quote"><Quote size={18} /></MenuButton>
      <MenuButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule"><Minus size={18} /></MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Media */}
      <MenuButton onClick={addLink} isActive={editor.isActive('link')} title="Add Link"><LinkIcon size={18} /></MenuButton>
      <MenuButton onClick={triggerUpload} title="Upload Image" disabled={uploading}>
        {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
      </MenuButton>
      <MenuButton onClick={addImageUrl} title="Image from URL"><ImageIcon size={18} /></MenuButton>

      {/* Image-specific wrap controls — only enabled when an image is selected */}
      <div className="w-full flex items-center gap-1 mt-1 pt-1 border-t border-gray-200">
        <span className={`text-xs px-2 ${imageSelected ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
          {imageSelected ? 'Image wrap:' : 'Select an image to set wrap'}
        </span>
        <button type="button" disabled={!imageSelected} onClick={() => applyImageWrap(editor, 'inline')}
          className="text-xs px-2 py-1 rounded hover:bg-blue-50 text-blue-700 disabled:opacity-40 disabled:hover:bg-transparent" title="In line with text">In-line</button>
        <button type="button" disabled={!imageSelected} onClick={() => applyImageWrap(editor, 'left')}
          className="text-xs px-2 py-1 rounded hover:bg-blue-50 text-blue-700 disabled:opacity-40 disabled:hover:bg-transparent" title="Wrap left (text flows on right)">Wrap left</button>
        <button type="button" disabled={!imageSelected} onClick={() => applyImageWrap(editor, 'right')}
          className="text-xs px-2 py-1 rounded hover:bg-blue-50 text-blue-700 disabled:opacity-40 disabled:hover:bg-transparent" title="Wrap right (text flows on left)">Wrap right</button>
        <button type="button" disabled={!imageSelected} onClick={() => applyImageWrap(editor, 'center')}
          className="text-xs px-2 py-1 rounded hover:bg-blue-50 text-blue-700 disabled:opacity-40 disabled:hover:bg-transparent" title="Center, full-width block">Center block</button>
      </div>
    </div>
  );
};

// Configure the Image extension so resizing via drag works (HTML width/height attrs are preserved).
const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: { default: 'tiptap-img tiptap-img-inline' },
      style: { default: null },
      width: { default: null },
      height: { default: null },
    };
  },
});

const RichTextEditor = ({ content, onChange, placeholder = "Start writing..." }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      ResizableImage.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-blue-600 underline' },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[300px] p-4 tiptap-content',
      },
      handleDrop(view, event, _slice, moved) {
        // Drag-and-drop image upload
        if (!moved && event.dataTransfer?.files?.length) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            uploadImageFile(file)
              .then((url) => {
                const { schema } = view.state;
                const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
                const node = schema.nodes.image.create({ src: url, class: 'tiptap-img tiptap-img-inline' });
                const transaction = view.state.tr.insert(coordinates ? coordinates.pos : 0, node);
                view.dispatch(transaction);
              })
              .catch((err) => alert(err.message));
            return true;
          }
        }
        return false;
      },
      handlePaste(view, event) {
        // Paste image upload
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              uploadImageFile(file)
                .then((url) => {
                  const { schema, tr, selection } = view.state;
                  const node = schema.nodes.image.create({ src: url, class: 'tiptap-img tiptap-img-inline' });
                  view.dispatch(tr.insert(selection.from, node));
                })
                .catch((err) => alert(err.message));
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden bg-white">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
